import styles from './investment-breakdown-widget.module.scss';
import {
  ChartOptions,
  ChartData,
  Chart as ChartJS,
  ActiveElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import DoughnutChartLegend, {
  CustomLegendHandle
} from './doughnut-chart-legend/DoughnutChartLegend';
import clsx from 'clsx';
import { Button, Icon, ICONS_ENUM } from '@sanlam-glacier/app-common/components';
import {
  doughnutChartColours,
  otherInvestmentName,
  otherSegmentColour
} from 'stores/dashboard-store/dashboard-store';
import { Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import WidgetLoader from '../../../../atoms/widget-loader/WidgetLoader';
import WidgetError from '../../../../atoms/widget-error/WidgetError';
import { useQuery } from '@tanstack/react-query';
import { getPortfolioSummary } from 'bff/hooks/reporting/PortfolioSummaryApi';
import { PortfolioSummaryViewModel } from 'bff';
import { InvestmentBreakdownDataPoint } from 'modules/dashboard/types/widgets/widget.interfaces';
import { WidgetContext } from 'modules/dashboard/components/organisms/widget/Widget';
import { useSetWidgetQueryStatus } from 'modules/dashboard/types/widgets/widget.hooks';
import { useBreakpoint } from 'providers/breakpoint/BreakpointProvider';
import { formatPercentageOfBook } from 'modules/dashboard/utils/formatting/currencyAndPercentageFormatter';

ChartJS.register(
  ArcElement,
  ChartTooltip,
  ChartLegend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  annotationPlugin
);

export function InvestmentBreakdownWidget() {
  const context = useContext(WidgetContext);
  const testComponentName = 'investmentBreakdown';

  const { isError, data, refetch, isFetching, isPending, isSuccess } = useQuery(
    getPortfolioSummary({ clientId: 1 })
  );

  useEffect(() => {
    if (!context) return;
    context.setReloadHandler(() => () => {
      refetch();
    });
  }, []);

  useSetWidgetQueryStatus({ isFetching, isError, isSuccess, context });

  const { breakpoint } = useBreakpoint();

  const chartRef = useRef<ChartJS | null>(null);
  const legendRef = useRef<CustomLegendHandle | null>(null);

  const [dataPointsForDoughnut, setDataPointsForDoughnut] = useState<
    InvestmentBreakdownDataPoint[] | undefined
  >(undefined);
  const [dataPointsForLegend, setDataPointsForLegend] = useState<
    InvestmentBreakdownDataPoint[] | undefined
  >(undefined);

  const [currentIndexOfHoveredSegment, setCurrentIndexOfHoveredSegment] = useState(-1);

  const formatChartData = useCallback((data: PortfolioSummaryViewModel) => {
    const resuableColours = doughnutChartColours;
    const otherColour = otherSegmentColour;

    let indexTrackerForColours = 0;

    const dataPointsForDoughnut: InvestmentBreakdownDataPoint[] = [];
    const dataPointsForLegend: InvestmentBreakdownDataPoint[] = [];

    if (!data) {
      return [dataPointsForDoughnut, dataPointsForLegend];
    }

    const investmentPlans = data.investmentPlans;
    const totalPercentageOfBook = investmentPlans.length * 100;

    let otherTotalPercentageOfBook = 0;
    let otherSumOfMarketValue = 0;

    for (const investmentPlan of investmentPlans) {
      for (const investmentFund of investmentPlan.investmentFunds) {
        const dataPoint: InvestmentBreakdownDataPoint = {
          planNumber: investmentPlan.investmentPlanNumber,
          investmentName: investmentFund.fundName,
          percentageOfBook: investmentFund.fundPercentage / totalPercentageOfBook,
          marketValue: investmentFund.fundMarketValue,
          highlighted: false
        };

        if (dataPoint.percentageOfBook < 0.05) {
          otherTotalPercentageOfBook += dataPoint.percentageOfBook;
          otherSumOfMarketValue += dataPoint.marketValue;

          dataPoint.color = otherColour;
          dataPointsForLegend.push(dataPoint);
        } else {
          dataPoint.color = resuableColours[indexTrackerForColours % resuableColours.length];

          dataPointsForDoughnut.push(dataPoint);
          dataPointsForLegend.push(dataPoint);
        }
        indexTrackerForColours++;
      }
    }

    const otherSegment: InvestmentBreakdownDataPoint = {
      planNumber: '',
      investmentName: otherInvestmentName,
      percentageOfBook: otherTotalPercentageOfBook,
      marketValue: otherSumOfMarketValue,
      color: otherColour,
      highlighted: false
    };
    dataPointsForDoughnut.push(otherSegment);

    dataPointsForLegend.sort((a, b) =>
      a.percentageOfBook > b.percentageOfBook ? -1 : a.percentageOfBook < b.percentageOfBook ? 1 : 0
    );

    return [dataPointsForDoughnut, dataPointsForLegend];
  }, []);

  useEffect(() => {
    if (data) {
      const [doughnutData, legendData] = formatChartData(data);
      setDataPointsForDoughnut(doughnutData);
      setDataPointsForLegend(legendData);
    }
  }, [data, formatChartData]);

  if (isFetching || isPending) return <WidgetLoader />;
  if (!dataPointsForDoughnut || !dataPointsForLegend || isError) {
    return <WidgetError onRefresh={refetch} isRefreshing={isFetching} />;
  }

  const doughnutData: ChartData = {
    labels: dataPointsForDoughnut.map((point) => point.investmentName),
    datasets: [
      {
        data: dataPointsForDoughnut.map((point) => point.percentageOfBook),
        backgroundColor: dataPointsForDoughnut.map((point) => point.color),
        borderWidth: 0
      }
    ]
  };

  const doughnutOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        backgroundColor: '#464E58',
        cornerRadius: 4,
        padding: 8,
        displayColors: false,
        callbacks: {
          title: () => {
            return '';
          },
          label: (context) => {
            let label = '';
            if (dataPointsForDoughnut[context.dataIndex]?.investmentName === otherInvestmentName) {
              label = `Other investments - ${formatPercentageOfBook(
                dataPointsForDoughnut[context.dataIndex]?.percentageOfBook,
                2
              )}`;
            } else {
              label = formatPercentageOfBook(
                dataPointsForDoughnut[context.dataIndex]?.percentageOfBook,
                2
              );
            }

            return label;
          }
        }
      },
      datalabels: {
        display: false
      },
      legend: {
        display: false
      },
      annotation: {
        annotations: {
          dLabel: {
            type: 'label',
            // empty content in the middle for aesthetic 'gap' purposes
            content: ['Investments', '', String(dataPointsForLegend.length)],
            font: [
              {
                size: breakpoint === 'md' ? 10 : 14,
                weight: 500,
                family: 'Roboto, Arial, sans-serif'
              },
              { size: breakpoint === 'md' ? 5 : 7 },
              {
                size: breakpoint === 'md' ? 18 : 24,
                weight: 500,
                family: 'Roboto, Arial, sans-serif'
              }
            ],
            color: ['#000000', '#000000', '#000000'],
            id: `${testComponentName}TotalInvesmentAnnotation`
          }
        }
      }
    },
    scales: {
      x: {
        display: false
      },
      y: {
        display: false
      }
    },
    onHover: (_event, elements) => {
      if (elements?.length > 0) {
        handleHover(elements);
      } else if (elements?.length <= 0 || currentIndexOfHoveredSegment !== -1) {
        handleOnMouseLeave();
      }
    }
  };

  return (
    <Suspense fallback={<WidgetLoader />}>
      {dataPointsForDoughnut && dataPointsForLegend ? (
        <div className={clsx(styles.doughnutWidget)}>
          <div className={clsx(styles.chartAndLegend)}>
            <div className={clsx(styles.doughnutChart)}>
              <Chart
                id={`${testComponentName}DoughnutChartCanvas`}
                data-testid={`${testComponentName}DoughnutChart`}
                type='doughnut'
                data={doughnutData}
                options={doughnutOptions}
                plugins={[ChartDataLabels]}
                ref={chartRef}
              />
            </div>

            <DoughnutChartLegend
              chartRef={chartRef}
              dataPointsForDoughnut={dataPointsForDoughnut}
              dataPointsForLegend={dataPointsForLegend}
              setDataPointsForLegend={setDataPointsForLegend}
              legendRef={legendRef}
            />
          </div>
          <Button
            data-testid={`${testComponentName}SeeFullPortfolioSummaryButton`}
            className={styles.seeSummaryBtn}
            //onClick={() => setFormVisible(true)}
            size={Button.SIZE.SMALL}
            variant={Button.VARIANT.SECONDARY}
            endIcon={
              <Icon icon={ICONS_ENUM.NAVIGATION_ARROW_FORWARD} className={styles.arrowIcon} />
            }
          >
            See full summary
          </Button>
        </div>
      ) : (
        <WidgetError onRefresh={refetch} isRefreshing={isFetching} />
      )}
    </Suspense>
  );

  function handleHover(elements: ActiveElement[]) {
    if (!elements.length || !dataPointsForDoughnut || !dataPointsForLegend) return;

    const indexOfHoveredSegment = elements[0]?.index;

    if (indexOfHoveredSegment === undefined) return;

    setCurrentIndexOfHoveredSegment((prevIndex) => {
      if (prevIndex === indexOfHoveredSegment) return prevIndex;

      const dataPointInChart = dataPointsForDoughnut[indexOfHoveredSegment];

      if (!dataPointInChart) return prevIndex;

      if (dataPointInChart.investmentName === otherInvestmentName) {
        setDataPointsForLegend((legendDataPoints) =>
          legendDataPoints?.map((dataPointInLegend, index) => {
            if (dataPointInLegend.color === otherSegmentColour) {
              legendRef.current?.scrollToLegendItem(index);
              return { ...dataPointInLegend, highlighted: true };
            }
            return { ...dataPointInLegend, highlighted: false };
          })
        );
      } else {
        setDataPointsForLegend((legendDataPoints) =>
          legendDataPoints?.map((dataPointInLegend, index) => {
            if (isEqual(dataPointInLegend, dataPointInChart)) {
              legendRef.current?.scrollToLegendItem(index);
              return { ...dataPointInLegend, highlighted: true };
            }
            return { ...dataPointInLegend, highlighted: false };
          })
        );
      }
      return indexOfHoveredSegment;
    });
  }

  function handleOnMouseLeave() {
    setCurrentIndexOfHoveredSegment(-1);
    setDataPointsForLegend((prevDataPoint) =>
      prevDataPoint?.map((dataPointInLegend) => ({
        ...dataPointInLegend,
        highlighted: false
      }))
    );
  }
}
